import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Wrench, CalendarCheck, Loader2, Phone, Mail, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { fittingAPI } from '../services/api';

// Public page for the partner centre (link received by e-mail) to confirm the mounting appointment
export default function FittingAppointmentPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [date, setDate] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { fittingAPI.appointment(token).then((d) => { setData(d); if (d.appointment?.date) setDate(d.appointment.date.slice(0, 16)); }).catch((e) => setError(e.response?.data?.detail || 'Lien invalide')); }, [token]);
  const requested = data?.appointment?.status === 'requested';

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try { setData(await fittingAPI.confirmAppointment(token, { date, note })); setDone(true); } catch (err) { setError(err.response?.data?.detail || 'Confirmation impossible'); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-10" data-testid="fitting-appointment-page">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 mb-2"><Wrench className="text-orange-500" /> Rendez-vous de montage</h1>
        {error && !data && <p className="text-red-600" data-testid="fa-error">{error}</p>}
        {data && (
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-5">
            <p className="text-sm text-gray-600">Commande <strong>{data.order_number}</strong> · {data.tyres} pneu(s) à monter chez <strong>{data.centre.name}</strong> ({data.centre.city}). Montage facturé au client : {Number(data.total).toFixed(2).replace('.', ',')} €.</p>
            <div className="rounded-lg bg-gray-50 p-4 text-sm">
              <p className="font-semibold text-gray-900 mb-1">Client</p>
              <p className="text-gray-700" data-testid="fa-customer">{data.customer.name}</p>
              <p className="text-gray-600 flex flex-wrap gap-4 mt-1">{data.customer.phone && <span className="inline-flex items-center gap-1"><Phone size={13} /> {data.customer.phone}</span>}{data.customer.email && <span className="inline-flex items-center gap-1"><Mail size={13} /> {data.customer.email}</span>}</p>
              <ul className="mt-3 text-gray-700 list-disc pl-5">{data.items.map((i, k) => <li key={k}>{i.quantity} × {i.name}</li>)}</ul>
            </div>
            {requested && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="fa-requested">
                <p className="font-semibold">Créneau souhaité par le client : {data.appointment.date_label}</p>
                <p className="mt-1 text-xs">Confirmez ce créneau tel quel, ou modifiez la date ci-dessous avant de confirmer : le client sera prévenu par e-mail.</p>
              </div>
            )}
            {data.appointment && !requested && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800 flex items-start gap-2" data-testid="fa-confirmed">
                <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                <div><p className="font-semibold">Rendez-vous confirmé : {data.appointment.date_label}</p>{data.appointment.note && <p className="mt-1">{data.appointment.note}</p>}<p className="mt-1 text-xs">{done ? 'Le client a été prévenu par e-mail.' : 'Vous pouvez modifier la date ci-dessous si nécessaire.'}</p></div>
              </div>
            )}
            <form onSubmit={submit} className="space-y-3">
              <label className="block text-sm font-medium text-gray-800">Date et heure du rendez-vous
                <Input type="datetime-local" required value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 h-10" data-testid="fa-date" />
              </label>
              <label className="block text-sm font-medium text-gray-800">Message pour le client (optionnel)
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex. : Merci de vous présenter 10 min avant, parking à l'arrière." className="mt-1 h-10" data-testid="fa-note" />
              </label>
              {error && <p className="text-sm text-red-600" data-testid="fa-error">{error}</p>}
              <Button type="submit" disabled={busy || !date} className="bg-orange-500 hover:bg-orange-600 h-11 px-6" data-testid="fa-submit">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CalendarCheck size={16} className="mr-2" />} {requested ? 'Confirmer ce rendez-vous' : data.appointment ? 'Modifier le rendez-vous' : 'Confirmer le rendez-vous'}</Button>
            </form>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
